import { Link, useNavigate } from "react-router-dom";
import Helpers from "../../Config/Helpers";
import { resolveLogoSrc } from "../../utils/resolveLogoSrc";

import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BuildIcon from "@mui/icons-material/Build";
import TireRepairIcon from "@mui/icons-material/TireRepair";
import OilBarrelIcon from "@mui/icons-material/OilBarrel";
import ExtensionIcon from "@mui/icons-material/Extension";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const ICON_COLOR = "#667eea";
const NAV_FONT_FAMILY = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
const DRAWER_WIDTH_OPEN = 260;
const DRAWER_WIDTH_CLOSED = 72;
const TRANSITION = "0.25s ease";

function NavItem({ to, IconComponent, label, onClick, collapsed }) {
  const button = (
    <ListItemButton
      component={to ? Link : "button"}
      to={to}
      onClick={onClick}
      sx={{
        borderRadius: "10px",
        mx: 1,
        mb: 0.5,
        justifyContent: collapsed ? "center" : "flex-start",
        px: collapsed ? 1.5 : 2,
        transition: `padding ${TRANSITION}, justify-content ${TRANSITION}`,
        "&:hover": {
          backgroundColor: "rgba(102, 126, 234, 0.12)",
          "& .MuiListItemIcon-root": { color: "#5a6fd6" },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 38,
          color: ICON_COLOR,
          justifyContent: "center",
          transition: `min-width ${TRANSITION}`,
          flexShrink: 0,
        }}
      >
        <IconComponent fontSize="small" />
      </ListItemIcon>

      {/* ── Label: always rendered, fades + slides on collapse ── */}
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: "14px",
          fontWeight: 500,
          color: "#000000",
          fontFamily: NAV_FONT_FAMILY,
          whiteSpace: "nowrap",
        }}
        sx={{
          opacity: collapsed ? 0 : 1,
          transform: collapsed ? "translateX(-8px)" : "translateX(0)",
          maxWidth: collapsed ? 0 : 200,
          overflow: "hidden",
          transition: `opacity ${TRANSITION}, transform ${TRANSITION}, max-width ${TRANSITION}`,
        }}
      />
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {collapsed ? (
        <Tooltip title={label} placement="right" arrow>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
}

function Navbar({ collapsed, setCollapsed }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    Helpers.toast("success", "Logged out successfully");
    navigate("/");
  };

  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../../images/logo-light.png");

  const isAdmin = Helpers.authUser.user_role == null;
  const drawerWidth = collapsed ? DRAWER_WIDTH_CLOSED : DRAWER_WIDTH_OPEN;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: `width ${TRANSITION}`,
        "@media print": { display: "none" },
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          background: "#ffffff",
          borderRight: "1px solid #e9ecf0",
          boxShadow: "2px 0 12px rgba(102,126,234,0.07)",
          overflowX: "hidden",
          transition: `width ${TRANSITION}`,
          "@media print": { display: "none" },
        },
      }}
    >
      {/* ── Brand / Logo + Toggle ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          py: 1.5,
          borderBottom: "1px solid #e9ecf0",
          minHeight: 64,
          transition: `padding ${TRANSITION}`,
        }}
      >
        {/* Logo: always rendered, fades + slides out when collapsed */}
        <Link
          to="/user/dashboard"
          replace
          style={{
            display: "flex",
            alignItems: "center",
            opacity: collapsed ? 0 : 1,
            transform: collapsed ? "translateX(-10px)" : "translateX(0)",
            maxWidth: collapsed ? 0 : 160,
            overflow: "hidden",
            pointerEvents: collapsed ? "none" : "auto",
            transition: `opacity ${TRANSITION}, transform ${TRANSITION}, max-width ${TRANSITION}`,
          }}
        >
          <img
            src={shopLogoSrc}
            alt="logo"
            style={{ height: 40, flexShrink: 0 }}
          />
        </Link>

        <IconButton
          size="small"
          onClick={() => setCollapsed((prev) => !prev)}
          sx={{
            color: ICON_COLOR,
            backgroundColor: "rgba(102,126,234,0.08)",
            flexShrink: 0,
            "&:hover": { backgroundColor: "rgba(102,126,234,0.18)" },
          }}
        >
          {collapsed ? (
            <MenuIcon fontSize="small" />
          ) : (
            <MenuOpenIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* ── "Menu" section label: always rendered, fades out ── */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 0.5,
          opacity: collapsed ? 0 : 1,
          maxHeight: collapsed ? 0 : 48,
          overflow: "hidden",
          transition: `opacity ${TRANSITION}, max-height ${TRANSITION}`,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "#000000",
            fontWeight: 600,
            letterSpacing: "0.1em",
            fontFamily: NAV_FONT_FAMILY,
          }}
        >
          Menu
        </Typography>
      </Box>

      {/* ── Nav Items ── */}
      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
          pb: 2,
          mt: collapsed ? 1 : 0,
          transition: `margin-top ${TRANSITION}`,
        }}
      >
        <List disablePadding>
          {isAdmin ? (
            <>
              <NavItem
                to="/user/dashboard"
                IconComponent={DashboardIcon}
                label="Dashboard"
                collapsed={collapsed}
              />
              <NavItem
                to="/checkin"
                IconComponent={AssignmentTurnedInIcon}
                label="Check In"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/appointments"
                IconComponent={CalendarMonthIcon}
                label="Appointments"
                collapsed={collapsed}
              />
               <NavItem
                to="/user/estimates"
                IconComponent={ReceiptIcon}
                label="Estimates"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/receipts"
                IconComponent={ReceiptIcon}
                label="Receipts"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/customers"
                IconComponent={PeopleAltIcon}
                label="Customers"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/services"
                IconComponent={BuildIcon}
                label="Services"
                collapsed={collapsed}
              />
                <NavItem
                to="/user/vehicles"
                IconComponent={DirectionsCarIcon}
                label="Vehicles"
                collapsed={collapsed}
              />
               <NavItem
                to="/user/parts"
                IconComponent={ExtensionIcon}
                label="Parts"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/tires"
                IconComponent={TireRepairIcon}
                label="Tires"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/oils"
                IconComponent={OilBarrelIcon}
                label="Oil And Filters"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/reports"
                IconComponent={BarChartIcon}
                label="Reports"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/settings"
                IconComponent={SettingsIcon}
                label="Settings"
                collapsed={collapsed}
              />
              <NavItem
            IconComponent={PowerSettingsNewIcon}
            label="Logout"
            onClick={handleLogout}
            collapsed={collapsed}
          />
              
            </>
          ) : (
            <>
              <NavItem
                to="/checkin"
                IconComponent={AssignmentTurnedInIcon}
                label="Check In"
                collapsed={collapsed}
              />
              <NavItem
                to="/user/appointments"
                IconComponent={CalendarMonthIcon}
                label="Appointments"
                collapsed={collapsed}
              />
              {/* {tabs.map((tab) =>
                canSee(tab.tab_link) ? (
                  <NavItem
                    key={tab._id}
                    to={tab.tab_link}
                    IconComponent={iconMap[tab.tab_icon] || BuildIcon}
                    label={tab.tab_name}
                    collapsed={collapsed}
                  />
                ) : null,
              )} */}
            </>
          )}
      
       
        </List>
      </Box>
    </Drawer>
  );
}

export default Navbar;
